"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  Wallet,
  X,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  faDateTime,
  faToman,
  formatCard,
} from "@/lib/format";
import type {
  CommissionWithdrawal,
  CommissionWithdrawalStatus,
} from "./types";

type Props = { onRefresh: () => void };
type Filter = "ALL" | CommissionWithdrawalStatus;

function StatusBadge({ status }: { status: CommissionWithdrawalStatus }) {
  if (status === "PAID")
    return (
      <Badge className="bg-primary/15 text-primary hover:bg-primary/20">
        پرداخت شده
      </Badge>
    );
  if (status === "REJECTED")
    return <Badge variant="destructive">رد شده</Badge>;
  return (
    <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/20 dark:text-amber-400">
      در انتظار
    </Badge>
  );
}

export default function CommissionPanel({ onRefresh }: Props) {
  const [withdrawals, setWithdrawals] = React.useState<
    CommissionWithdrawal[]
  >([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<Filter>("ALL");

  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [rejectTarget, setRejectTarget] =
    React.useState<CommissionWithdrawal | null>(null);
  const [rejectNote, setRejectNote] = React.useState("");
  const [rejecting, setRejecting] = React.useState(false);

  const [payingId, setPayingId] = React.useState<string | null>(null);

  const loadWithdrawals = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/commission", { cache: "no-store" });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در دریافت تسویه‌ها");
      }
      setWithdrawals(data.withdrawals ?? []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در دریافت تسویه‌ها";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadWithdrawals();
  }, [loadWithdrawals]);

  const filtered = React.useMemo(() => {
    return withdrawals.filter(
      (w) => filter === "ALL" || w.status === filter,
    );
  }, [withdrawals, filter]);

  function openReject(w: CommissionWithdrawal) {
    setRejectTarget(w);
    setRejectNote(w.adminNote ?? "");
    setRejectOpen(true);
  }

  async function payWithdrawal(w: CommissionWithdrawal) {
    setPayingId(w.id);
    try {
      const res = await fetch("/api/admin/commission", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: w.id, action: "pay" }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در ثبت پرداخت");
      }
      toast.success("تسویه به‌عنوان پرداخت‌شده ثبت شد.");
      loadWithdrawals();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در ثبت پرداخت";
      toast.error(msg);
    } finally {
      setPayingId(null);
    }
  }

  async function submitReject(e: React.FormEvent) {
    e.preventDefault();
    if (!rejectTarget) return;
    if (!rejectNote.trim()) {
      toast.error("برای رد درخواست، دلیل را وارد کنید.");
      return;
    }
    setRejecting(true);
    try {
      const res = await fetch("/api/admin/commission", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: rejectTarget.id,
          action: "reject",
          adminNote: rejectNote.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در رد درخواست");
      }
      toast.success("درخواست تسویه رد شد.");
      setRejectOpen(false);
      loadWithdrawals();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در رد درخواست";
      toast.error(msg);
    } finally {
      setRejecting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card className="rounded-2xl border-border/60 shadow-sm">
        <CardHeader className="border-b">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="size-5 text-primary" />
              درخواست‌های تسویه کمیسیون
            </CardTitle>
            <Select
              value={filter}
              onValueChange={(v) => setFilter(v as Filter)}
            >
              <SelectTrigger className="h-9 w-40 text-sm">
                <SelectValue placeholder="همه" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">همه</SelectItem>
                <SelectItem value="PENDING">در انتظار</SelectItem>
                <SelectItem value="PAID">پرداخت شده</SelectItem>
                <SelectItem value="REJECTED">رد شده</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded-md" />
              ))}
            </div>
          ) : error ? (
            <div className="py-8 text-center text-sm text-destructive">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              درخواست تسویه‌ای یافت نشد.
            </div>
          ) : (
            <div className="max-h-[28rem] overflow-y-auto scrollbar-thin">
              <Table>
                <TableHeader className="sticky top-0 bg-card">
                  <TableRow>
                    <TableHead className="pr-4">کاربر</TableHead>
                    <TableHead>مبلغ</TableHead>
                    <TableHead>شماره کارت</TableHead>
                    <TableHead>وضعیت</TableHead>
                    <TableHead>تاریخ</TableHead>
                    <TableHead className="text-center">عملیات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((w) => (
                    <TableRow key={w.id}>
                      <TableCell className="pr-4">
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {w.user?.name ?? "—"}
                          </span>
                          <span
                            dir="ltr"
                            className="text-xs text-muted-foreground text-right"
                          >
                            {w.user?.phone ?? "—"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm font-semibold text-primary">
                        {faToman(w.amountToman)}
                      </TableCell>
                      <TableCell
                        dir="ltr"
                        className="font-mono text-xs text-muted-foreground"
                      >
                        {w.cardNumber ? formatCard(w.cardNumber) : "—"}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={w.status} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {faDateTime(w.createdAt)}
                      </TableCell>
                      <TableCell>
                        {w.status === "PENDING" ? (
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="sm"
                              className="bg-primary hover:bg-primary/90"
                              disabled={payingId === w.id}
                              onClick={() => payWithdrawal(w)}
                            >
                              {payingId === w.id ? (
                                <Loader2 className="size-3.5 animate-spin" />
                              ) : (
                                <CheckCircle2 className="size-3.5" />
                              )}
                              پرداخت شد
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => openReject(w)}
                            >
                              <X className="size-3.5" />
                              رد
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {w.adminNote ? w.adminNote : "—"}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject dialog */}
      <Dialog
        open={rejectOpen}
        onOpenChange={(o) => !o && setRejectOpen(false)}
      >
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <X className="size-5" />
              رد درخواست تسویه
            </DialogTitle>
            <DialogDescription>
              {rejectTarget?.user?.name
                ? `درخواست تسویه ${rejectTarget.user.name} رد خواهد شد.`
                : "این درخواست تسویه رد خواهد شد."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReject} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="reject-reason">دلیل رد *</Label>
              <Textarea
                id="reject-reason"
                placeholder="مثلاً: شماره کارت نامعتبر است."
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                disabled={rejecting}
                className="min-h-24 text-sm"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRejectOpen(false)}
                disabled={rejecting}
              >
                انصراف
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={rejecting}
              >
                {rejecting ? "در حال رد..." : "تأیید رد"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
